import type { AgenticHubPayload, CanonicalSearchLoad, ClientCall, EmailAgentMode } from '@numeo/types'
import { AgenticHubAction, resolveUnifiedSearchSources } from '@numeo/types'
import { canonicalCodesToUnified, shouldCollapseToNumeoSearch, UNIFIED_EQUIPMENT_LOADBOARD_MAPPING } from '@numeo/types-core'
import axios from 'axios'
import { generalAgentServiceAxios, getAuthToken, loadsIngestServiceAxios } from 'setup/SetupAxios'
import type {
    AgentSettings,
    BookedLoadHistoryItem,
    BookItTemplatePair,
    BrokerInquiry,
    LoadsSort,
    MyBrokerGroup,
    MyBrokerRateConf,
    MyBrokersSearchParams,
    SearchSseCallbacks,
} from '../types/api'

export type { BookedLoadHistoryItem, BrokerInquiry, LoadsSort, MyBrokerGroup, MyBrokerRateConf }

// main-service — only for email thread display (not agentic hub)
const mainServiceAxios = axios

/** Consume one AI Hub search quota. Returns remaining count or null if unlimited. */
export const consumeAiHubSearchQuota = async (): Promise<{ allowed: boolean; remaining: number | null }> => {
    try {
        const res = await mainServiceAxios.post<{ success: boolean; remaining?: number }>('/ai-hub-search-consume')
        return { allowed: true, remaining: res.data.remaining ?? null }
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 429) {
            return { allowed: false, remaining: 0 }
        }
        return { allowed: true, remaining: null }
    }
}

// ─── Messages & Requests (general-agent-service) ─────────────────────────────
// All return 202 Accepted and stream via Socket.io — except a search-agent send, which can
// read its turn back on the request itself (SSE, see streamSearchMessage).

/**
 * Send a user message to the chat-agent or search-agent lane.
 *
 * Endpoints are disjoint per backend convention (#7969 r3242144548): chat goes to
 * `/tabs/:tabId/messages`, search goes to `/tabs/:tabId/search-messages`. This
 * function picks the right one based on `agentKind` so callers can use a single
 * API surface without remembering which path serves which lane.
 *
 * `structured_search_filters_json` is forwarded only on the chat path — the
 * search-agent doesn't accept pre-extracted filters as input.
 *
 * `searchStream` (search lane only): the turn streams back on this request (SSE)
 * instead of arriving over the socket.
 */
export async function sendMessage(
    tabId: string,
    message: string,
    modelName: string,
    autoNegotiate: boolean,
    autoEmail: boolean,
    senderEmail?: string,
    structuredSearchFiltersJson?: string,
    agentKind?: 'chat' | 'search',
    searchStream?: SearchTurnStream
) {
    const path = agentKind === 'search' ? `/tabs/${tabId}/search-messages` : `/tabs/${tabId}/messages`
    const body: Record<string, unknown> = {
        message,
        model_name: modelName,
        auto_negotiate: autoNegotiate,
        auto_email: autoEmail,
        sender_email: senderEmail,
    }
    if (agentKind !== 'search') {
        body.structured_search_filters_json = structuredSearchFiltersJson
    }
    if (agentKind === 'search' && searchStream) return streamSearchMessage({ tabId, message }, path, JSON.stringify(body), searchStream)
    const response = await generalAgentServiceAxios.post(path, body)
    return response?.data
}

/** A search-agent send's own event stream (SSE): each event of the turn, and whether the stream was cut early. */
export interface SearchTurnStream {
    onEvent: (event: AgenticHubPayload) => void
    /** The stream ended (dropped or closed) before the turn's terminal event. */
    onInterrupted: (turn: { tabId: string; message: string }) => void
}

/** The events that end a search-agent turn; a stream that closes before one of them was cut mid-turn. */
const SEARCH_TURN_END: ReadonlySet<AgenticHubAction> = new Set([AgenticHubAction.AH_StreamEnd, AgenticHubAction.AH_Error, AgenticHubAction.AH_Superseded])

/**
 * POST a search-agent message and read its turn back on the same request (SSE) rather than the socket.
 * Resolves once the server accepted it (response headers — the 202's contract), then keeps reading in the
 * background. A plain JSON reply (stream off server-side) leaves the turn on the socket, as before.
 */
async function streamSearchMessage(turn: { tabId: string; message: string }, path: string, body: string, stream: SearchTurnStream): Promise<void> {
    const baseUrl = (generalAgentServiceAxios.defaults.baseURL ?? '').replace(/\/$/, '')
    const token = getAuthToken()
    const resp = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body,
    })
    if (!resp.ok) {
        const error = (await resp.json().catch(() => null)) as { message?: string } | null
        throw new Error(error?.message || `Send failed: ${resp.status} ${resp.statusText}`)
    }
    if (!resp.body || !resp.headers.get('content-type')?.includes('text/event-stream')) return
    let ended = false
    void readSseJson<AgenticHubPayload>(resp.body, (event) => {
        if (SEARCH_TURN_END.has(event.action)) ended = true
        try {
            stream.onEvent(event)
        } catch (err) {
            console.error('[AH] Failed to handle search-agent stream event', err, { action: event.action })
        }
    })
        .catch((err) => console.warn('[AH] Search-agent stream dropped', err))
        .then(() => {
            if (!ended) stream.onInterrupted(turn)
        })
}

/**
 * Read an SSE body frame by frame (`event: <type>\ndata: <json>\n\n`) and hand each frame's parsed `data:`
 * JSON to `onFrame`. Malformed frames are skipped; what a frame means, and what a throwing handler does,
 * stays with the caller.
 */
async function readSseJson<T>(body: ReadableStream<Uint8Array>, onFrame: (data: T) => void): Promise<void> {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    try {
        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const frames = buffer.split('\n\n')
            buffer = frames.pop() ?? ''
            for (const frame of frames) {
                const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
                if (!dataLine) continue
                let data: T
                try {
                    data = JSON.parse(dataLine.slice(5).trim())
                } catch {
                    continue // malformed — skip
                }
                onFrame(data)
            }
        }
    } finally {
        reader.releaseLock()
    }
}

export async function initiateCall(tabId: string, postingId: string, context?: string): Promise<{ callId: string; roomName: string }> {
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: { callId: string; roomName: string } }>(`/tabs/${tabId}/calls/initiate`, {
        postingId,
        context,
    })
    return response.data.data
}

export async function respondToRequest(tabId: string, requestId: string, selectedOptionId: string | string[], note?: string, editedBody?: string) {
    const body: Record<string, unknown> = { note: note || '' }
    if (Array.isArray(selectedOptionId)) {
        body.selected_option_ids = selectedOptionId
        body.selected_option_id = selectedOptionId[0] || ''
    } else {
        body.selected_option_id = selectedOptionId
    }
    if (editedBody !== undefined) body.edited_body = editedBody
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/requests/${requestId}/respond`, body)
    return response?.data
}

// ─── Tabs (general-agent-service) ────────────────────────────────────────────

export async function fetchTabs() {
    const response = await generalAgentServiceAxios.get('/tabs')
    return response?.data
}

/** Per-tab unread + open-request counts, so every tab's badge shows upfront. Falls back to [] if the
 *  backend predates the pending_count field (during a deploy) — badges then degrade to lazy-on-open. */
export async function fetchTabCounts(): Promise<{ tab_id: string; unread_count: number; pending_count: number }[]> {
    try {
        const response = await generalAgentServiceAxios.get('/tabs/unread-summary')
        return response?.data?.data ?? []
    } catch {
        return []
    }
}

export async function fetchRecentSearches(): Promise<{ success: boolean; data?: Array<{ search_form: unknown; last_used_at: string }> }> {
    const response = await generalAgentServiceAxios.get('/tabs/recent-searches')
    return response?.data
}

export async function createTab(title?: string, senderEmail?: string, modelName?: string) {
    const response = await generalAgentServiceAxios.post('/tabs', { title, sender_email: senderEmail, model_name: modelName })
    return response?.data
}

export async function deleteTab(tabId: string) {
    const response = await generalAgentServiceAxios.delete(`/tabs/${tabId}`)
    return response?.data
}

export async function shutdownAgent(tabId: string) {
    const response = await generalAgentServiceAxios.patch(`/tabs/${tabId}`, { status: 'shutdown', started: false })
    return response?.data
}

export async function enableAgent(tabId: string) {
    const response = await generalAgentServiceAxios.patch(`/tabs/${tabId}`, { status: 'active', started: true })
    return response?.data
}

/**
 * Hard-cancel every in-flight run for this tab. Only caller is the tab-close
 * path (deleteTab / deleteAllTabs); the backend always treats this as a force
 * cancel — committed runs (email-agent post-Gmail-send work) are torn down
 * too. Returns `{ success, cancelled }` — `cancelled` may be 0 if the run
 * ended between tab-close and the request landing.
 */
export async function cancelTabRuns(tabId: string): Promise<{ success: boolean; cancelled: number }> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/cancel`)
    const data = response?.data
    // Defensive shape check — if the backend reports `success: false` (or returns nothing),
    // throw so the caller's .catch surfaces it instead of silently claiming the run was stopped.
    if (!data || data.success !== true) {
        throw new Error(typeof data?.message === 'string' ? data.message : 'Failed to cancel tab runs')
    }
    return data
}

/**
 * Mirrors the relevant slice of `controllers/tabs.ts:updateTabHandler` response. Only the
 * fields the client currently reads off the post-write tab are listed — extend as needed.
 * Defined inline (not lifted to a shared type) to avoid a circular import with the
 * `TabApiResponse` definition in utils/agenticHubHelpers.
 */
interface UpdateTabResponse {
    success: boolean
    data?: {
        mode?: EmailAgentMode
        model_name?: string | null
        status?: string
    }
    message?: string
}

export async function updateTab(tabId: string, updates: Record<string, unknown>): Promise<UpdateTabResponse | undefined> {
    const response = await generalAgentServiceAxios.patch<UpdateTabResponse>(`/tabs/${tabId}`, updates)
    return response?.data
}

// ─── Messages & Requests Read (general-agent-service) ────────────────────────

export async function fetchMessages(tabId: string, page: number, limit: number, sort: 'asc' | 'desc' = 'asc', surface?: 'chat' | 'search') {
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/messages`, {
        params: { page, limit, sort, ...(surface ? { surface } : {}) },
    })
    return response?.data
}

export interface AgentWorkspaceFile {
    path: '/plan.md' | '/tasks.md'
    exists: boolean
    content: string | null
    modified_at: string | null
}

export async function fetchAgentWorkspace(tabId: string): Promise<{ success: boolean; data?: { files?: AgentWorkspaceFile[] } }> {
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/agent-workspace`)
    return response?.data
}

// Backend ignores the `surface` param and a single call returns every pending request for the
// tab. The previous chat+email fan-out + client-side dedupe was 2× HTTP for the same payload.
export async function fetchActiveRequests(tabId: string) {
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/requests`, {
        params: { status: 'pending' },
    })
    return response?.data
}

// Fetches a single load by posting_id (external_load_id). Used to recover a load that
// referenced by a pending agent request but isn't in the locally-cached loads page —
// returning null when the server has no record (purged or never existed).
export async function fetchLoadByPostingId(tabId: string, postingId: string): Promise<CanonicalSearchLoad | null> {
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/loads/by-posting-id`, {
        params: { posting_id: postingId },
    })
    return (response?.data?.data as CanonicalSearchLoad | null) ?? null
}

// ─── Loads (general-agent-service) ───────────────────────────────────────────

export async function fetchLoads(
    tabId: string,
    filter: 'all' | 'with_response' | 'archived' = 'all',
    page = 1,
    limit = 50,
    search?: string,
    sort?: LoadsSort,
    statuses?: string[]
) {
    const params: Record<string, unknown> = { filter, page, limit }
    if (search) params.search = search
    if (sort) params.sort = sort
    if (statuses && statuses.length > 0) params.statuses = statuses.join(',')
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/loads`, { params })
    return response?.data
}

export async function softDeleteLoads(tabId: string, loadIds: string[]): Promise<{ success: boolean; data: { deleted: string[]; not_found: string[] } }> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/loads/soft-delete`, { load_ids: loadIds })
    return response?.data
}

export async function restoreLoads(
    tabId: string,
    loadIds: string[]
): Promise<{ success: boolean; data: { restored: Array<{ id: string; restored_at: string }>; not_found: string[] } }> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/loads/restore`, { load_ids: loadIds })
    return response?.data
}

export async function toggleEmailProcessing(tabId: string, threadId: string, enabled: boolean) {
    const response = await generalAgentServiceAxios.patch(`/tabs/${tabId}/loads/${encodeURIComponent(threadId)}/email-processing`, { enabled })
    return response?.data
}

export async function resetUnreadCount(emailThreadId: string) {
    const response = await generalAgentServiceAxios.post('/cross-tab/loads/reset-unread', { email_thread_id: emailThreadId })
    return response?.data
}

export async function markLoadAsRead(tabId: string, rowNumber: number) {
    const response = await generalAgentServiceAxios.patch(`/tabs/${tabId}/loads/${rowNumber}`, { is_read: true })
    return response?.data
}

export async function pinLoad(tabId: string, postingId: string, pinned: boolean) {
    const response = await generalAgentServiceAxios.patch(`/tabs/${tabId}/loads/by-posting-id`, { posting_id: postingId, is_pinned: pinned })
    return response?.data
}

// ─── Email (general-agent-service) ───────────────────────────────────────────

/** Send a template-based email for a load (email icon button) */
export async function sendLoadEmail(tabId: string, postingId: string) {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/loads/${encodeURIComponent(postingId)}/send-email`)
    return response?.data
}

/** Send a manual reply in an existing email thread (inline compose) */
export async function sendEmail(tabId: string, postingId: string, body: string, subject?: string) {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/loads/${encodeURIComponent(postingId)}/send-email`, {
        body,
        subject,
    })
    return response?.data
}

/** Book It — dispatcher pre-authorizes booking; backend kicks off the email agent with a booking_authorization decision. */
export async function bookLoad(tabId: string, postingId: string) {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/loads/${encodeURIComponent(postingId)}/book-it`)
    return response?.data
}

/** Cross-tab booking history — every load in this project that the agent flipped to status='booked'. */
export async function listBookedLoads(opts: { page?: number; limit?: number; search?: string } = {}) {
    const params: Record<string, string | number> = {}
    if (opts.page) params.page = opts.page
    if (opts.limit) params.limit = opts.limit
    if (opts.search) params.search = opts.search
    const response = await generalAgentServiceAxios.get<{
        success: boolean
        data: { items: BookedLoadHistoryItem[]; pagination: { total: number; page: number; limit: number } }
    }>('/cross-tab/loads/booked', { params })
    return response?.data
}

// ─── Calls (general-agent-service) ───────────────────────────────────────────

export async function fetchCallsByIds(tabId: string, callIds: string[]) {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/calls`, { callIds })
    return response?.data
}

/** Re-hydrate a single call on mount/reload — used by `useAgenticHubCall` to repopulate
 *  the transcript panel after page refresh. Replaces main-service's `GET /ext/calls/:callId`. */
export async function fetchCallById(callId: string): Promise<ClientCall | null> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: { call: ClientCall } }>(`/calls/${callId}`)
    return response?.data?.data?.call ?? null
}

export async function fetchActiveAiHubCalls(): Promise<ClientCall[]> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: { calls: ClientCall[] } }>('/calls/active')
    return response?.data?.data?.calls ?? []
}

/** Mint a LiveKit token for the "Listen" / "Take Over" buttons. GAS looks up the room
 *  from the call row, so the client only needs `callId`. Same token serves both modes —
 *  the client gates audio via track muting and `setMicrophoneEnabled`.
 */
export async function requestParticipantToken(callId: string): Promise<{ token: string; serverURL: string }> {
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: { token: string; serverURL: string } }>(`/calls/${callId}/participant-token`)
    return response.data.data
}

/** Hang up an active call. */
export async function endCall(callId: string): Promise<void> {
    await generalAgentServiceAxios.post(`/calls/${callId}/end`)
}

// ─── Settings (general-agent-service) ────────────────────────────────────────

export async function getAgentSettings(): Promise<{ success: boolean; data: AgentSettings }> {
    const response = await generalAgentServiceAxios.get('/settings')
    return response?.data
}

export async function saveAgentSettings(settings: AgentSettings): Promise<{ success: boolean }> {
    const response = await generalAgentServiceAxios.put('/settings', settings)
    return response?.data
}

export async function getSignature() {
    const response = await generalAgentServiceAxios.get('/settings/signature')
    return response?.data
}

export async function saveSignature(signature: string) {
    const response = await generalAgentServiceAxios.put('/settings/signature', { signature })
    return response?.data
}

export async function getBrokerOutreachTemplate(): Promise<{ success: boolean; data: { subject: string; body: string } }> {
    const response = await generalAgentServiceAxios.get('/settings/broker-outreach-template')
    return response?.data
}

export async function saveBrokerOutreachTemplate(subject: string, body: string): Promise<{ success: boolean; data: { subject: string; body: string } }> {
    const response = await generalAgentServiceAxios.put('/settings/broker-outreach-template', { subject, body })
    return response?.data
}

export async function getBookItTemplate(): Promise<{ success: boolean; data: BookItTemplatePair }> {
    const response = await generalAgentServiceAxios.get('/settings/book-it-template')
    return response?.data
}

export async function saveBookItTemplate(templates: BookItTemplatePair): Promise<{ success: boolean; data: BookItTemplatePair }> {
    const response = await generalAgentServiceAxios.put('/settings/book-it-template', templates)
    return response?.data
}

export async function getMyBrokersEnabled(): Promise<{ success: boolean; data?: { enabled?: boolean } }> {
    const response = await generalAgentServiceAxios.get('/settings/my-brokers-enabled')
    return response?.data
}

export async function getSimulatedBroker() {
    const response = await generalAgentServiceAxios.get('/settings/simulated-broker')
    return response?.data
}

export async function saveSimulatedBroker(enabled: boolean) {
    const response = await generalAgentServiceAxios.put('/settings/simulated-broker', { enabled })
    return response?.data
}

/**
 * Search via SSE — emits loads_batch per DAT cursor page (~150 loads, ~4s each),
 * then all_complete when all pages are done. No socket involvement for load delivery.
 *
 * Pass an AbortSignal to cancel the stream mid-flight (e.g. when the user closes the
 * tab the search is streaming into). Aborting causes the reader loop to exit cleanly
 * without delivering further batches.
 */
/**
 * Translate the ai-hub's snake_case search params (from generateSearchParams) into
 * the unified endpoint's body. LIS only needs the SEARCH fields (origin/dest, sources,
 * equipment, dates, weight/length, factoring). The SCORING fields (min_rate, target_rpm,
 * other_preferences, structured filters, driver_id, …) are NOT sent to LIS — they ride
 * on `search_form`, which ai-hub persists on the tab (updateTab) and the GAS receiver
 * reads post-persist for scoring. We still forward search_form so it's on the wire too.
 */
function resolvedSourcesOf(p: Record<string, unknown>): string[] {
    return resolveUnifiedSearchSources(p.sources as string[] | undefined, {
        collapseToNumeoSearch: shouldCollapseToNumeoSearch(
            { states: p.origin_states as string[] | undefined },
            { states: p.destination_states as string[] | undefined, zones: p.destination_zones as string[] | undefined }
        ),
    })
}

export function toUnifiedSearchBody(tabId: string, params: Record<string, unknown>): Record<string, unknown> {
    const p = params
    const origin: Record<string, unknown> = {}
    if (p.origin_city) origin.city = p.origin_city
    if (p.origin_state) origin.state = p.origin_state
    if (p.origin_lat != null) origin.latitude = p.origin_lat
    if (p.origin_lng != null) origin.longitude = p.origin_lng
    if (p.origin_states) origin.states = p.origin_states
    if (p.max_origin_deadhead_miles != null) origin.radiusMiles = p.max_origin_deadhead_miles

    const destination: Record<string, unknown> = {}
    if (p.destination_city) destination.city = p.destination_city
    if (p.destination_state) destination.state = p.destination_state
    if (p.destination_lat != null) destination.latitude = p.destination_lat
    if (p.destination_lng != null) destination.longitude = p.destination_lng
    if (p.destination_states) destination.states = p.destination_states
    if (p.destination_zones) destination.zones = p.destination_zones
    if (p.max_destination_deadhead_miles != null) destination.radiusMiles = p.max_destination_deadhead_miles

    // Source policy is shared with every other unified-search client (mobile
    // LoadBoard/Home/Radar) via @numeo/types: the user's picks, normalized, plus
    // the implicit NUMEO_SEARCH baseline. The collapse rule is the shared
    // shouldCollapseToNumeoSearch: only a multi-state/zone ORIGIN or an over-cap
    // destination collapses to NUMEO_SEARCH — a destination within the fan-out
    // cap keeps the picked boards (LIS fans them out per state). AI Hub falls
    // back to the baseline alone (no default board set) — NUMEO_SEARCH spans
    // every ingested source already.
    const sources = resolvedSourcesOf(p)

    return {
        // Agentic identity: LIS persists agent_loads + pings the GAS receiver.
        agentic: true,
        tabId,
        // Opt-in GAS post-persist ping (LIS default is off). The web ai-hub WANTS GAS to run its
        // pipeline (scoring, thread update) after persistence, so it must request the ping explicitly.
        pingGas: true,
        // DAT token: ai-hub uses the shared random-token pool (NOT the customer's own
        // AIHub account) — the legacy GAS proxy ran ai-hub searches with the random pool
        // (useRandomDatToken=true on the service-key path). usePersonalDatToken=false maps
        // to useRandomDatToken=true, preserving that behaviour.
        usePersonalDatToken: false,
        // ai-hub meters its own quota (consumeAiHubSearchQuota); don't double-count.
        count_against_quota: false,
        // LIS attaches Market Intel to each load (`marketIntel` lane rate + `destinationMarket` reload market, ADR-033).
        includeMarketData: true,
        paginate: true,
        // Search fields (unified shape).
        ...(Object.keys(origin).length ? { origin } : {}),
        ...(Object.keys(destination).length ? { destination } : {}),
        sources,
        // Equipment: send BOTH the raw codes (equipmentTypes, canonical V/F/R...) AND
        // the unified codes (unifiedEquipmentTypes, DRY_VAN...), exactly like the GAS
        // client did. LIS's getProviders filters loadboards on unifiedEquipmentTypes —
        // sending canonical codes there drops DAT/TRUCKSTOP (they don't match unified
        // keys). Convert canonical→unified via the shared @numeo/types helper (pass
        // through if already unified).
        ...(() => {
            const codes = p.equipment_types as string[] | undefined
            if (!codes?.length) return {}
            const allUnified = codes.every((c) => UNIFIED_EQUIPMENT_LOADBOARD_MAPPING[c] !== undefined)
            return { equipmentTypes: codes, unifiedEquipmentTypes: allUnified ? codes : canonicalCodesToUnified(codes) }
        })(),
        ...(p.earliest_pickup ? { earliestPickup: p.earliest_pickup } : {}),
        ...(p.latest_pickup ? { latestPickup: p.latest_pickup } : {}),
        ...(p.full_partial ? { fullPartial: p.full_partial } : {}),
        ...(p.max_weight_pounds != null ? { weight: p.max_weight_pounds } : {}),
        ...(p.max_length_feet != null ? { length: p.max_length_feet } : {}),
        ...(p.max_age_minutes != null ? { maxAgeMinutes: p.max_age_minutes } : {}),
        ...(p.factoring_provider ? { factoringProvider: p.factoring_provider } : {}),
        ...(p.factoring_filter ? { factoringFilter: { minGrade: (p.factoring_filter as { min_grade?: string }).min_grade } } : {}),
        ...(p.excludeBlacklisted != null ? { excludeBlacklisted: p.excludeBlacklisted } : {}),
        // search_form carries the SCORING params (min_rate, target_rpm, preferences, …)
        // the GAS receiver reads post-persist. v2 search-flow is now gated solely by the
        // per-user Flipt flag (client-agnostic), so we no longer stamp `client: 'web'`.
        search_form: { ...((p.search_form as Record<string, unknown> | undefined) ?? {}) },
    }
}

/**
 * Run an AI-Hub load search over SSE. The tab's arm, pinned by general-agent-service from the Flipt flag `ai-hub-stream-search` when
 * the tab's first search starts, picks the endpoint: `stream` searches through general-agent-service, `legacy` through the LIS unified
 * endpoint. The legacy GAS `search-and-filter` proxy is retired (Unify Search 3c). See docs/ADR/load-pipeline/ADR-040.4-Search-Aggregator.md.
 */
export async function searchAndFilterSSE(tabId: string, params: Record<string, unknown>, callbacks: SearchSseCallbacks, signal?: AbortSignal): Promise<void> {
    if ((await searchArmOf(tabId)) === 'stream') return searchStreamSSE(tabId, params, callbacks, signal)
    return searchUnifiedSSE(tabId, params, callbacks, signal)
}

type SearchArm = 'legacy' | 'stream'

/**
 * Which side searches for this tab. Asking pins it, so the answer never changes for the tab. A service that cannot answer (the route is
 * not deployed: a 404) reads as `legacy`, the search the tab has always run, so GAS not yet updated changes nothing. Any other failure is
 * thrown: a tab already pinned to `stream` must not fall back to `search-unified`, which would write rows into it and open the old live feed.
 */
async function searchArmOf(tabId: string): Promise<SearchArm> {
    try {
        const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/search-arm`)
        return response?.data?.data?.arm === 'stream' ? 'stream' : 'legacy'
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) return 'legacy'
        throw error
    }
}

/** One frame of general-agent-service's search stream; each frame's JSON carries its own `type`. */
interface SearchStreamEvent {
    type?: string
    loads?: CanonicalSearchLoad[]
    source?: string
    error?: string
}

/**
 * The search on general-agent-service, for a tab on the `stream` arm: it persists each batch to the tab before sending it, so a
 * `loads_batch` frame means the rows are queryable now, and it scores in process after the last one. The body is the search's own
 * params plus the boards the dispatcher's picks resolve to; the scoring fields ride on `search_form`.
 */
async function searchStreamSSE(tabId: string, params: Record<string, unknown>, callbacks: SearchSseCallbacks, signal?: AbortSignal): Promise<void> {
    const baseUrl = (generalAgentServiceAxios.defaults.baseURL ?? '').replace(/\/$/, '')
    const token = getAuthToken()
    const resp = await fetch(`${baseUrl}/tabs/${tabId}/search-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...params, sources: resolvedSourcesOf(params) }),
        signal,
    })
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw new Error(`search stream failed: ${resp.status} ${resp.statusText} — ${text.slice(0, 200)}`)
    }
    if (!resp.body) throw new Error('No response body for the search stream')

    await readSseJson<SearchStreamEvent>(resp.body, (event) => {
        switch (event.type) {
            case 'loads_batch':
                callbacks.onBatch({ loads: event.loads ?? [] })
                break
            case 'all_complete':
                callbacks.onComplete()
                break
            case 'source_error':
                callbacks.onSourceError?.({ source: event.source, error: event.error })
                break
            case 'error':
                callbacks.onError?.(new Error(event.error || 'Search pipeline error'))
                break
        }
    })
}

/**
 * NEW path — admin ai-hub calls the LIS unified endpoint directly with agentic=true.
 * LIS streams unscored loads + persists them, then pings the GAS
 * /internal/search-complete receiver, which scores async (poll + sockets unchanged).
 * Emits source_start / source_complete / source_error / all_complete / error.
 */
async function searchUnifiedSSE(tabId: string, params: Record<string, unknown>, callbacks: SearchSseCallbacks, signal?: AbortSignal): Promise<void> {
    const baseUrl = (loadsIngestServiceAxios.defaults.baseURL ?? '').replace(/\/$/, '')
    const token = getAuthToken()
    const resp = await fetch(`${baseUrl}/loads/search-unified`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(toUnifiedSearchBody(tabId, params)),
        signal,
    })
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw new Error(`search-unified SSE failed: ${resp.status} ${resp.statusText} — ${text.slice(0, 200)}`)
    }
    if (!resp.body) throw new Error('No response body for search-unified SSE')

    // The unified endpoint emits source_start / source_complete / source_error / all_complete / error,
    // each frame's JSON carrying its own `type`. Consume those natively (no remapping to a legacy event
    // shape) and drive the existing callbacks.
    await readSseJson<UnifiedSearchEvent>(resp.body, (event) => {
        switch (event.type) {
            case 'source_complete':
                callbacks.onBatch({
                    loads: event.loads ?? [],
                    source: event.source,
                    total: typeof event.total === 'number' ? event.total : undefined,
                    totalAvailable: typeof event.totalAvailable === 'number' ? event.totalAvailable : undefined,
                })
                break
            case 'all_complete':
                callbacks.onComplete()
                break
            case 'source_error':
                callbacks.onSourceError?.({ source: event.source, error: event.error })
                break
            case 'error':
                callbacks.onError?.(new Error(event.error || 'Search pipeline error'))
                break
            // source_start (and any future events) are no-ops for this consumer.
        }
    })
}

/** One unified-endpoint frame; each event's JSON carries its own `type`. */
interface UnifiedSearchEvent {
    type?: string
    loads?: CanonicalSearchLoad[]
    source?: string
    total?: number
    totalAvailable?: number
    error?: string
}

export async function pollTab(tabId: string) {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/poll`)
    return response?.data
}

// ─── Email Threads (main-service — email-service integration) ────────────────

export async function fetchEmailsByThreadId(threadId: string) {
    const response = await mainServiceAxios.get(`/email/threadId/${encodeURIComponent(threadId)}`)
    return response?.data
}

// Returns a map of emailAddress (lowercased) -> footerStartPhrase for collapsing signatures.
export async function fetchBrokerFooters(emails: string[]): Promise<Record<string, string>> {
    if (emails.length === 0) return {}
    const response = await mainServiceAxios.get<{ success: boolean; data: Record<string, string> }>('/email/broker-footers', {
        params: { emails: emails.join(',') },
    })
    return response?.data?.data ?? {}
}

export async function dismissLoadRequest(tabId: string, requestId: string) {
    await generalAgentServiceAxios.delete(`/tabs/${tabId}/requests/${requestId}`)
}
export async function reportEmailProblem(data: { threadId?: string; loadId?: string; tabId?: string; message?: string; source?: string }) {
    const response = await mainServiceAxios.post('/email/report-problem', data)
    return response?.data
}

export async function autonomousWakeup(tabId: string): Promise<{
    skipped: boolean
    nextRunAt?: string
    lastRunAt?: string
    reason?: string
    /** Stamped server-side on first heartbeat for FullyAutonomous tabs.
     *  FE uses this to compute the 5h cutoff locally and stop calling the
     *  endpoint once the window elapses. BE doesn't enforce the cutoff. */
    autonomousStartedAt?: string
}> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/autonomous-wakeup`)
    return response?.data
}

export async function resetAutonomousRun(tabId: string): Promise<{ success: boolean; autonomousStartedAt: string }> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/autonomous-reset`)
    return response?.data
}

// ─── My Brokers (main-service) ───────────────────────────────────────────────

export async function searchMyBrokers(params: MyBrokersSearchParams): Promise<{ success: boolean; data: MyBrokerGroup[]; count: number }> {
    const response = await mainServiceAxios.get('/my-brokers/search', { params })
    return response?.data
}

// ─── Broker outreach (general-agent-service → email-service) ─────────────────

export async function sendBrokerOutreach(
    tabId: string,
    params: {
        recipient_email: string
        recipient_name?: string
        subject: string
        body: string
        /** MC number — identity for dedupe. If missing, the email sends but no inquiry row is saved. */
        broker_mc?: string | null
        broker_name?: string | null
    }
): Promise<{
    success: boolean
    data?: { email_thread_id: string; broker_email: string; broker_mc: string | null; already_sent?: boolean }
    message?: string
}> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/broker-outreach`, params)
    return response?.data
}

// ─── Broker inquiries (persisted outreach log — replaces localStorage) ───────

export async function listBrokerInquiries(tabId: string): Promise<{ success: boolean; data: BrokerInquiry[] }> {
    const response = await generalAgentServiceAxios.get(`/tabs/${tabId}/broker-inquiries`)
    return response?.data
}

/** Reply to an existing broker-inquiry thread — no load context, just threadId + body. */
export async function sendInquiryReply(
    tabId: string,
    params: { thread_id: string; body: string }
): Promise<{ success: boolean; data?: { thread_id: string; broker_email: string }; message?: string }> {
    const response = await generalAgentServiceAxios.post(`/tabs/${tabId}/broker-inquiries/reply`, params)
    return response?.data
}
